import { type ScaleLinear } from "d3";
import { debounce } from "lodash-es";
import { equals } from "ramda";
import { PureComponent } from "react";
import { connect } from "react-redux";
import { notificationDismiss } from "widgets";

import { getExtendedRange, rangeContainsRange, rangeEquals } from "../../../app-utils/range.js";
import { bedShapePileupParams } from "../../../app-utils/track-shapes/shapes-settings.js";
import { type IPileup, PileupFactory } from "../../../app-utils/track-shapes/track-pileup-factory.js";
import { getPileupHeight } from "../../../app-utils/track-shapes/track-utils.js";
import {
  type Contig,
  type CoordinateList,
  type Feature,
  type Range,
  type Track,
  type TrackCoordinateOrderedCoordinateList,
  type TrackFeatures,
} from "../../../graphql.js";
import { notify } from "../../../modules/notifications/notify.js";
import {
  fetchTrackCoordinateFeatures,
  getTrackId,
  type IGenomeViewerStateLayout,
  type ITrackData,
  loadCoordinateList,
} from "../../../redux/modules/genome-browser.js";
import { type IAppState } from "../../../redux/reducer.js";
import { DEFAULT_TRACK_HEIGHT } from "../../../settings/settings.js";
import { type SetTooltipContent } from "../GenomeViewerTracksList.js";
import { CoordinateTrack } from "./CoordinateTrack.js";

export interface ICoordinateTrackData extends TrackFeatures, ITrackData {
  showAllFeatures: boolean;
}

export const INITIAL_COORDINATE_TRACK_DATA: Partial<ICoordinateTrackData> = {
  coordinateListId: null,
  features: [],
  showAllFeatures: false,
};

interface ICoordinateTrackLoaderOwnProps {
  track: Track; // track spec
  coordinateListData: TrackCoordinateOrderedCoordinateList;
  scale: ScaleLinear<number, number>;
  setTooltipContent: SetTooltipContent;
}

interface ICoordinateTrackLoaderAppStateProps {
  trackData: ICoordinateTrackData;
  range: Range;
  contig: Contig;
  layout: IGenomeViewerStateLayout;
  coordList: CoordinateList;
}

interface ICoordinateTrackLoaderDispatchProps {
  loadCoordinateList: (coordinateListId: number) => Promise<CoordinateList>;
  showWarning: (warningMessage: string, coordinateListId: string | number) => void;
  fetchTrackCoordinateFeatures: (
    track: Track,
    contig: Contig,
    range: Range,
    coordinateListId: number,
  ) => Promise<TrackFeatures>;
}

interface ICoordinateTrackLoaderProps
  extends ICoordinateTrackLoaderAppStateProps,
    ICoordinateTrackLoaderDispatchProps,
    ICoordinateTrackLoaderOwnProps {}

function mapStateToProps(
  state: IAppState,
  ownProps: ICoordinateTrackLoaderOwnProps,
): ICoordinateTrackLoaderAppStateProps {
  const trackId: string = getTrackId({ ...ownProps.track, fileId: ownProps.coordinateListData.coordinateListId });

  return {
    range: state.genomeBrowser.range,
    contig: state.genomeBrowser.contig,
    trackData: state.genomeBrowser.trackDataMap[trackId] as ICoordinateTrackData,
    layout: state.genomeBrowser.layout,
    coordList: state.genomeBrowser.coordinateLists[ownProps.coordinateListData.coordinateListId],
  };
}

function mapDispatchToProps(dispatch): ICoordinateTrackLoaderDispatchProps {
  return {
    loadCoordinateList: (coordinateListId: number) => dispatch(loadCoordinateList(coordinateListId)),
    fetchTrackCoordinateFeatures: (track: Track, contig: Contig, range: Range, coordinateListId: number) =>
      dispatch(fetchTrackCoordinateFeatures(track, contig, range, coordinateListId)),
    showWarning: (warningMessage: string, coordinateListId) =>
      notify.warning(
        {
          title: "Mismatch",
          children: warningMessage,
        },
        { autoClose: false, toastId: coordinateListId },
      ),
  };
}

interface ICoordinateTrackLoaderState {
  pileup: IPileup;
  trackMessage: string;
}

class CoordinateTrackLoader extends PureComponent<ICoordinateTrackLoaderProps, ICoordinateTrackLoaderState> {
  state = {
    pileup: null,
    trackMessage: null,
  };
  private pileupFactory: PileupFactory;

  loadData = () => {
    const coordList = this.props.coordList;
    if (coordList && !coordList.hidden && !coordList.deleted) {
      const extendedRange = getExtendedRange(this.props.contig, this.props.range);
      void this.props.fetchTrackCoordinateFeatures(
        this.props.track,
        this.props.contig,
        extendedRange,
        this.props.coordinateListData.coordinateListId,
      );
    }
  };

  debouncedLoadData = debounce(this.loadData, 300, { maxWait: 1000 });

  loadCoordinateList = () => {
    void this.props
      .loadCoordinateList(this.props.coordinateListData.coordinateListId)
      .then((coordList: CoordinateList) => {
        if (coordList?.hidden) {
          this.setState({ trackMessage: "This coordinate list is not accessible to you." });
        } else if (coordList?.deleted) {
          this.setState({ trackMessage: "This coordinate list has been deleted." });
        } else {
          this.debouncedLoadData();
        }
      });
  };

  componentDidMount() {
    this.pileupFactory = new PileupFactory(this.props.scale);

    this.loadCoordinateList();
  }

  componentDidUpdate(
    prevProps: Readonly<ICoordinateTrackLoaderProps>,
    _prevState: Readonly<ICoordinateTrackLoaderState>,
  ) {
    if (this.props.scale !== prevProps.scale) {
      this.pileupFactory = new PileupFactory(this.props.scale);
    }

    const rangeDiffers = !rangeEquals(prevProps.range, this.props.range);
    const contigDiffers: boolean = prevProps.contig?.id !== this.props.contig?.id;
    const boxModelDiffers = !equals(this.props.layout, prevProps.layout);

    // logic about data fetching
    if (rangeDiffers || contigDiffers) {
      const missingData: boolean =
        !this.props.trackData?.range ||
        !rangeContainsRange(this.props.trackData?.range, this.props.range) ||
        this.props.trackData?.overflow;

      if (contigDiffers) {
        // directly fetch data again
        this.loadData();
      } else if (missingData) {
        // debounced data fetching until range is stabilized
        this.debouncedLoadData();
      }
    }

    const trackDataDiffers: boolean = this.props.trackData !== prevProps.trackData;

    // update the layout if the data do not overflow
    if (!this.props.trackData?.overflow) {
      if (trackDataDiffers || rangeDiffers) {
        this.updateLayout(this.props.trackData?.features);
      } else if (boxModelDiffers) {
        this.debouncedUpdateLayout(this.props.trackData?.features);
      }
    }
  }
  componentWillUnmount() {
    notificationDismiss(this.props.coordinateListData.coordinateListId);
  }

  updateLayout = (coordinateFeatures: Feature[]) => {
    if (coordinateFeatures == undefined) {
      return;
    }

    const pileup: IPileup = this.pileupFactory.buildFeaturePileup(
      coordinateFeatures,
      this.props.range,
      bedShapePileupParams,
      this.props.trackData?.showAllFeatures,
      0,
    );

    this.setState({ pileup: pileup });
  };

  debouncedUpdateLayout = debounce(this.updateLayout, 300, { maxWait: 1000 });

  getTrackHeight = () => {
    return this.props.trackData?.overflow ? DEFAULT_TRACK_HEIGHT : getPileupHeight(this.state.pileup);
  };

  render() {
    return (
      <CoordinateTrack
        track={this.props.track}
        coordinateListData={this.props.coordinateListData}
        featureColor={this.props.coordinateListData.color}
        trackHeight={this.getTrackHeight()}
        pileup={this.state.pileup}
        overflow={this.props.trackData?.overflow}
        empty={false}
        showAllFeatures={this.props.trackData?.showAllFeatures}
        setTooltipContent={this.props.setTooltipContent}
        trackMessage={this.state.trackMessage}
      />
    );
  }
}

const SmartCoordinateTrackLoader = connect<
  ICoordinateTrackLoaderAppStateProps,
  ICoordinateTrackLoaderDispatchProps,
  ICoordinateTrackLoaderOwnProps
>(
  mapStateToProps,
  mapDispatchToProps,
)(CoordinateTrackLoader);

export { SmartCoordinateTrackLoader as CoordinateTrackLoader };
